/**
 * Zoom for the contract preview: fit-to-width by default, or a fixed scale
 * the viewer picks (remembered in this browser). The sheet is scaled with
 * a CSS transform, so `getBoundingClientRect` stays in visual pixels and
 * scroll-to-section math works at any zoom.
 */

import { useCallback, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { ArrowLeftRight, Minus, Plus } from 'lucide-react';
import {
  SHEET_GUTTER_PX,
  SHEET_WIDTH_PX,
  ZOOM_STORAGE_KEY,
  canStep,
  fitScale,
  formatZoom,
  parseZoom,
  resolveScale,
  scaledLayout,
  serializeZoom,
  stepZoom,
  type PreviewZoom,
} from '../lib/preview-zoom.js';

export function usePreviewZoom(): { zoom: PreviewZoom; setZoom: (z: PreviewZoom) => void } {
  const [zoom, setZoomState] = useState<PreviewZoom>(() => {
    try {
      return parseZoom(window.localStorage.getItem(ZOOM_STORAGE_KEY));
    } catch {
      return 'fit';
    }
  });
  const setZoom = useCallback((z: PreviewZoom) => {
    setZoomState(z);
    try {
      window.localStorage.setItem(ZOOM_STORAGE_KEY, serializeZoom(z));
    } catch {
      // Storage unavailable — the choice lasts for this page view only.
    }
  }, []);
  return { zoom, setZoom };
}

const BTN =
  'inline-flex h-6 items-center justify-center rounded px-1.5 text-stone-600 transition-colors hover:bg-stone-100 hover:text-stone-900 disabled:cursor-not-allowed disabled:opacity-40';

export function PreviewZoomBar({
  zoom,
  scale,
  onChange,
  children,
}: {
  zoom: PreviewZoom;
  scale: number;
  onChange: (z: PreviewZoom) => void;
  children?: ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-2 border-b border-stone-200 bg-white px-3 py-1 text-[11px] text-stone-500">
      <div className="min-w-0">{children}</div>
      <div className="flex shrink-0 items-center gap-0.5" role="group" aria-label="Zoom för förhandsvisning">
        <button
          type="button"
          className={BTN}
          aria-label="Zooma ut"
          title="Zooma ut"
          disabled={!canStep(scale, -1)}
          onClick={() => onChange(stepZoom(scale, -1))}
        >
          <Minus className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          className={BTN + ' min-w-[3.25rem] tabular-nums'}
          title="Återställ till 100 %"
          onClick={() => onChange(1)}
        >
          {formatZoom(scale)}
        </button>
        <button
          type="button"
          className={BTN}
          aria-label="Zooma in"
          title="Zooma in"
          disabled={!canStep(scale, 1)}
          onClick={() => onChange(stepZoom(scale, 1))}
        >
          <Plus className="h-3.5 w-3.5" />
        </button>
        <span className="mx-1 h-4 w-px bg-stone-200" aria-hidden="true" />
        <button
          type="button"
          className={BTN + ' gap-1' + (zoom === 'fit' ? ' bg-stone-100 text-stone-900' : '')}
          aria-pressed={zoom === 'fit'}
          onClick={() => onChange('fit')}
        >
          <ArrowLeftRight className="h-3.5 w-3.5" />
          Anpassa till bredd
        </button>
      </div>
    </div>
  );
}

/**
 * In-page (legacy React) preview: a zoom bar over a scroll container
 * (`data-preview-scroll`, which the editor's scroll-to-section uses) that
 * holds the sheet scaled to the chosen zoom.
 */
export function ScaledPreview({ children }: { children: ReactNode }) {
  const { zoom, setZoom } = usePreviewZoom();
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const innerRef = useRef<HTMLDivElement | null>(null);
  const [avail, setAvail] = useState(0);
  const [innerHeight, setInnerHeight] = useState(0);

  useLayoutEffect(() => {
    const scroller = scrollRef.current;
    const inner = innerRef.current;
    if (!scroller || !inner) return;
    const measure = () => {
      setAvail(scroller.clientWidth);
      setInnerHeight(inner.offsetHeight);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(scroller);
    ro.observe(inner);
    return () => ro.disconnect();
  }, []);

  const fit = fitScale(avail);
  const scale = avail > 0 ? resolveScale(zoom, fit) : 1;
  const { layoutWidth, visualWidth } = scaledLayout(avail || SHEET_WIDTH_PX, scale);

  const prevScale = useRef(scale);
  useLayoutEffect(() => {
    const el = scrollRef.current;
    const prev = prevScale.current;
    prevScale.current = scale;
    if (!el || prev === scale) return;
    el.scrollTop *= scale / prev;
    el.scrollLeft *= scale / prev;
  }, [scale]);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <PreviewZoomBar zoom={zoom} scale={scale} onChange={setZoom} />
      <div ref={scrollRef} data-preview-scroll className="min-h-0 flex-1 overflow-auto">
        <div className="overflow-hidden" style={{ width: visualWidth, height: innerHeight * scale }}>
          <div
            ref={innerRef}
            style={{
              width: layoutWidth,
              padding: SHEET_GUTTER_PX,
              transform: `scale(${scale})`,
              transformOrigin: '0 0',
            }}
          >
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}
